import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  MapIcon,
  GlobeAmericasIcon,
  ShieldExclamationIcon,
  CircleStackIcon,
  ArrowPathIcon,
  TableCellsIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { redliningApi } from '../lib/api';
import { abbreviateInstitution } from '../lib/institution';
import { useTeamId } from '../hooks/useTeamId';
import FeatureGate from '../components/FeatureGate';
import clsx from 'clsx';

const RISK_LEVEL_BADGE: Record<string, string> = {
  LOW: 'bg-green-100 text-green-700',
  MODERATE: 'bg-yellow-100 text-yellow-700',
  HIGH: 'bg-red-100 text-red-700',
  CRITICAL: 'bg-red-200 text-red-800',
};

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 5 }, (_, i) => currentYear - i);

export default function RedliningAnalysisPage() {
  // Default to current filing year (matches backend); user can change to historical years
  const [selectedYear, setSelectedYear] = useState<number>(currentYear);

  // Team
  const { teamId } = useTeamId();

  const yearParams = { year: selectedYear };

  // Standalone redlining workpaper export (R1/R9/R12).
  const [exporting, setExporting] = useState<'pdf' | 'xlsx' | null>(null);
  const exportRedlining = async (format: 'pdf' | 'xlsx') => {
    if (!teamId) return;
    setExporting(format);
    try {
      const res = await redliningApi.exportAnalysis(teamId, format);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Redlining_Analysis.${format}`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e: any) {
      toast.error(
        e?.response?.status === 404
          ? 'No redlining analysis to export yet — run an FLD with R9/R12 first.'
          : 'Export failed',
      );
    } finally {
      setExporting(null);
    }
  };

  // Summary
  const { data: summaryData } = useQuery({
    queryKey: ['redlining-summary', teamId, selectedYear],
    queryFn: () => redliningApi.getSummary(teamId!, yearParams),
    enabled: !!teamId,
  });
  const summary = summaryData?.data;

  // Origination Map
  const { data: originationData, isLoading: originationLoading } = useQuery({
    queryKey: ['redlining-origination-map', teamId, selectedYear],
    queryFn: () => redliningApi.getOriginationMap(teamId!, yearParams),
    enabled: !!teamId,
  });
  const originations = originationData?.data?.tracts || [];

  // Minority Comparison
  const { data: minorityData, isLoading: minorityLoading } = useQuery({
    queryKey: ['redlining-minority-comparison', teamId, selectedYear],
    queryFn: () => redliningApi.getMinorityComparison(teamId!, yearParams),
    enabled: !!teamId,
  });
  const minority = minorityData?.data;

  // CRA Assessment
  const { data: craData, isLoading: craLoading } = useQuery({
    queryKey: ['redlining-cra-assessment', teamId, selectedYear],
    queryFn: () => redliningApi.getCraAssessment(teamId!, yearParams),
    enabled: !!teamId,
  });
  const craAssessment = craData?.data;

  return (
    <FeatureGate feature="redlining_analysis" mode="lock">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Redlining Analysis</h1>
            <p className="mt-1 text-sm text-gray-500">
              Analyze lending patterns across census tracts for fair lending compliance
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => exportRedlining('pdf')}
              disabled={exporting !== null}
              className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              title="Export the R1/R9/R12 redlining workpaper as a PDF"
            >
              <ArrowDownTrayIcon className="h-4 w-4" />
              {exporting === 'pdf' ? 'Exporting…' : 'PDF'}
            </button>
            <button
              onClick={() => exportRedlining('xlsx')}
              disabled={exporting !== null}
              className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              title="Export the R1/R9/R12 redlining workpaper as an Excel workbook"
            >
              <ArrowDownTrayIcon className="h-4 w-4" />
              {exporting === 'xlsx' ? 'Exporting…' : 'Excel'}
            </button>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500"
            >
              {YEAR_OPTIONS.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <div className="rounded-lg border bg-white p-4">
            <div className="flex items-center gap-2">
              <GlobeAmericasIcon className="h-5 w-5 text-blue-500" />
              <p className="text-xs font-medium text-gray-500">Total Originations</p>
            </div>
            <p className="mt-1 text-2xl font-bold text-gray-900">
              {summary?.totalOriginations ?? 0}
            </p>
          </div>
          <div className="rounded-lg border bg-white p-4">
            <div className="flex items-center gap-2">
              <MapIcon className="h-5 w-5 text-purple-500" />
              <p className="text-xs font-medium text-gray-500">FFIEC Reference Tracts</p>
            </div>
            {summary?.censusTractCount ? (
              <p className="mt-1 text-2xl font-bold text-gray-900">{summary.censusTractCount.toLocaleString()}</p>
            ) : (
              <p className="mt-1 text-sm font-medium text-gray-400 italic">Not loaded for {selectedYear}</p>
            )}
          </div>
          <div className="rounded-lg border bg-white p-4">
            <div className="flex items-center gap-2">
              <ShieldExclamationIcon className="h-5 w-5 text-red-500" />
              <p className="text-xs font-medium text-gray-500">Risk Level</p>
            </div>
            <p className="mt-1">
              {minority?.riskLevel ? (
                <span className={clsx('inline-flex rounded-full px-2.5 py-0.5 text-sm font-bold', RISK_LEVEL_BADGE[minority.riskLevel] || RISK_LEVEL_BADGE.LOW)}>
                  {minority.riskLevel}
                </span>
              ) : (
                <span className="text-2xl font-bold text-gray-400">--</span>
              )}
            </p>
          </div>
          <div className="rounded-lg border bg-white p-4">
            <div className="flex items-center gap-2">
              <CircleStackIcon className="h-5 w-5 text-green-500" />
              <p className="text-xs font-medium text-gray-500">Reference Data Available</p>
            </div>
            <p className="mt-1 text-2xl font-bold text-gray-900">
              {summary?.censusReferenceAvailable ? 'Yes' : 'No'}
            </p>
          </div>
        </div>

        {/* R1 VBAA Screen */}
        <R1VbaaScreen teamId={teamId!} />

        {/* Origination Map */}
        <div className="rounded-lg border bg-white">
          <div className="border-b px-4 py-3">
            <h3 className="text-sm font-semibold text-gray-900">Origination Map - Top Tracts</h3>
            <p className="text-xs text-gray-400 mt-0.5">
              Lending distribution by census tract for {selectedYear}
            </p>
          </div>

          {originationLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" />
            </div>
          ) : originations.length === 0 ? (
            <div className="py-12 text-center text-gray-500">
              <MapIcon className="mx-auto h-12 w-12 text-gray-300" />
              <p className="mt-4 text-lg font-medium">No origination data</p>
              <p className="mt-1 text-sm">Upload HMDA data to populate origination analysis.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Tract Code</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">State</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">County</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Originations</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Total Amount</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Avg Loan</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Majority-Minority</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Income Level</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {originations.map((t: any, i: number) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-gray-900">{t.tractCode}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-600">{t.state || '--'}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-600">{t.county || '--'}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-900">{t.originations}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-900">
                        {t.totalAmount != null ? `$${t.totalAmount.toLocaleString()}` : '--'}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-600">
                        {t.avgLoanAmount != null ? `$${Math.round(t.avgLoanAmount).toLocaleString()}` : '--'}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3">
                        {t.isMajorityMinority == null ? (
                          <span className="italic text-xs text-gray-400">No FFIEC ref</span>
                        ) : (
                          <span className={clsx(
                            'inline-flex rounded-full px-2 py-0.5 text-xs font-medium',
                            t.isMajorityMinority ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'
                          )}>
                            {t.isMajorityMinority ? 'Yes' : 'No'}
                          </span>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm">
                        {t.tractIncomeLevel
                          ? <span className="text-gray-600">{t.tractIncomeLevel}</span>
                          : <span className="italic text-xs text-gray-400">No FFIEC ref</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Minority Comparison */}
        <div className="rounded-lg border bg-white">
          <div className="border-b px-4 py-3">
            <h3 className="text-sm font-semibold text-gray-900">Minority Comparison</h3>
            <p className="text-xs text-gray-400 mt-0.5">
              Comparison of lending in majority-minority vs non-minority areas
            </p>
          </div>

          {minorityLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" />
            </div>
          ) : !minority?.comparison ? (
            <div className="py-8 text-center text-sm text-gray-400">No comparison data available.</div>
          ) : (
            <div className="p-4 space-y-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {/* Majority-Minority Areas */}
                <div className="rounded-lg border bg-purple-50 p-4">
                  <h4 className="text-sm font-semibold text-purple-900">Majority-Minority Areas</h4>
                  <dl className="mt-2 space-y-1">
                    <div className="flex justify-between">
                      <dt className="text-sm text-purple-700">Originations</dt>
                      <dd className="text-sm font-medium text-purple-900">{minority.comparison.majorityMinority?.count ?? 0}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-sm text-purple-700">Share</dt>
                      <dd className="text-sm font-medium text-purple-900">{minority.comparison.majorityMinority?.pct ?? 0}%</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-sm text-purple-700">Total Amount</dt>
                      <dd className="text-sm font-medium text-purple-900">
                        {minority.comparison.majorityMinority?.totalAmount != null
                          ? `$${minority.comparison.majorityMinority.totalAmount.toLocaleString()}`
                          : '--'}
                      </dd>
                    </div>
                  </dl>
                </div>

                {/* Non-Minority Areas */}
                <div className="rounded-lg border bg-gray-50 p-4">
                  <h4 className="text-sm font-semibold text-gray-900">Non-Minority Areas</h4>
                  <dl className="mt-2 space-y-1">
                    <div className="flex justify-between">
                      <dt className="text-sm text-gray-600">Originations</dt>
                      <dd className="text-sm font-medium text-gray-900">{minority.comparison.nonMinority?.count ?? 0}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-sm text-gray-600">Share</dt>
                      <dd className="text-sm font-medium text-gray-900">{minority.comparison.nonMinority?.pct ?? 0}%</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-sm text-gray-600">Total Amount</dt>
                      <dd className="text-sm font-medium text-gray-900">
                        {minority.comparison.nonMinority?.totalAmount != null
                          ? `$${minority.comparison.nonMinority.totalAmount.toLocaleString()}`
                          : '--'}
                      </dd>
                    </div>
                  </dl>
                </div>
              </div>

              {/* Area Context */}
              {minority.areaContext && (
                <div className="rounded-lg border bg-blue-50 p-4">
                  <h4 className="text-sm font-semibold text-blue-900">Area Context</h4>
                  <p className="mt-1 text-sm text-blue-700">
                    {minority.areaContext.totalTracts ?? 0} census tracts in reference data,{' '}
                    {minority.areaContext.majorityMinorityTracts ?? 0} classified as majority-minority ({minority.areaContext.areaMmPct ?? 0}%)
                  </p>
                </div>
              )}

              {/* Risk Flag */}
              {minority.riskFlag && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-4">
                  <div className="flex items-center gap-2">
                    <ShieldExclamationIcon className="h-5 w-5 text-red-600" />
                    <h4 className="text-sm font-semibold text-red-900">Risk Flag</h4>
                  </div>
                  <p className="mt-1 text-sm text-red-700">{minority.riskFlag}</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* CRA Assessment */}
        <div className="rounded-lg border bg-white">
          <div className="border-b px-4 py-3">
            <h3 className="text-sm font-semibold text-gray-900">CRA Assessment Area Analysis</h3>
            <p className="text-xs text-gray-400 mt-0.5">
              Income distribution of lending activity within the assessment area
            </p>
          </div>

          {craLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" />
            </div>
          ) : !craAssessment ? (
            <div className="py-8 text-center text-sm text-gray-400">No CRA assessment data available.</div>
          ) : (
            <div className="p-4 space-y-4">
              {/* Income Distribution Table */}
              {craAssessment.incomeDistribution && craAssessment.incomeDistribution.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Income Level</th>
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Originations</th>
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Pct</th>
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Total Amount</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {craAssessment.incomeDistribution.map((d: any, i: number) => (
                        <tr key={i} className="hover:bg-gray-50">
                          <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-gray-900">{d.incomeLevel}</td>
                          <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-900">{d.originations}</td>
                          <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-600">
                            {d.pct != null ? `${d.pct.toFixed(1)}%` : '--'}
                          </td>
                          <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-900">
                            {d.totalAmount != null ? `$${d.totalAmount.toLocaleString()}` : '--'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* LMI % and Assessment Text */}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {craAssessment.lmiOriginationPct != null && (
                  <div className="rounded-lg border bg-gray-50 p-4">
                    <p className="text-xs font-medium text-gray-500">LMI Lending Percentage</p>
                    <p className={clsx(
                      'mt-1 text-2xl font-bold',
                      craAssessment.lmiOriginationPct >= 30 ? 'text-green-600' :
                      craAssessment.lmiOriginationPct >= 15 ? 'text-yellow-600' : 'text-red-600'
                    )}>
                      {craAssessment.lmiOriginationPct.toFixed(1)}%
                    </p>
                  </div>
                )}
                {craAssessment.assessment && (
                  <div className="rounded-lg border bg-blue-50 p-4">
                    <p className="text-xs font-medium text-blue-800">Assessment</p>
                    <p className="mt-1 text-sm text-blue-700">{craAssessment.assessment}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </FeatureGate>
  );
}

// ── R1 VBAA Screen Component ──────────────────────────────────────────────────

const R1_YEARS = Array.from({ length: 5 }, (_, i) => currentYear - 1 - i);

function R1VbaaScreen({ teamId }: { teamId: string }) {
  const queryClient = useQueryClient();
  const [selectedYears, setSelectedYears] = useState<number[]>([
    currentYear - 1, currentYear - 2, currentYear - 3,
  ]);

  const { data: r1Data, isLoading: r1Loading } = useQuery({
    queryKey: ['r1-screen', teamId, selectedYears],
    queryFn: () => redliningApi.getR1Screen(teamId, selectedYears),
    enabled: !!teamId && selectedYears.length > 0,
  });
  const r1 = r1Data?.data;
  // Subject-bank column header — dynamic per the team's configured institution
  // (replaces the former hardcoded "TBoFC" placeholder).
  const subjectLabel = abbreviateInstitution(r1?.institutionName);
  const subjectName = r1?.institutionName?.trim() || 'Subject bank';

  const ingestMutation = useMutation({
    mutationFn: () => redliningApi.ingestR1(teamId, selectedYears),
    onSuccess: (res) => {
      const data = res?.data ?? {};
      const skipped: Array<{ year: number; reason: string }> = data.skipped ?? [];
      if (skipped.length) {
        const skippedYears = [...new Set(skipped.map(s => s.year))].sort().join(', ');
        toast.success(
          `VBAA ingested (${data.lendersIngested ?? 0} lenders). Skipped ${skippedYears} — not yet available from CFPB.`,
          { duration: 6000 },
        );
      } else {
        toast.success(`VBAA data ingested (${data.lendersIngested ?? 0} lenders) — computing R1 screen...`);
      }
      queryClient.invalidateQueries({ queryKey: ['r1-screen'] });
    },
    onError: () => toast.error('Failed to ingest VBAA data'),
  });

  const toggleYear = (year: number) => {
    setSelectedYears(prev =>
      prev.includes(year) ? prev.filter(y => y !== year) : [...prev, year].sort((a, b) => b - a),
    );
  };

  // Group rows by assessment area
  const grouped = new Map<string, any[]>();
  for (const row of r1?.rows ?? []) {
    const key = row.assessmentAreaName ?? row.assessmentAreaId;
    if (!grouped.has(key)) grouped.set(key, []);
    grouped.get(key)!.push(row);
  }

  const sortedYears = [...selectedYears].sort((a, b) => b - a);

  return (
    <div className="rounded-lg border bg-white">
      <div className="flex items-center justify-between border-b px-4 py-3">
        <div className="flex items-center gap-2">
          <TableCellsIcon className="h-5 w-5 text-indigo-500" />
          <div>
            <h3 className="text-sm font-semibold text-gray-900">
              R1 Screen — VBAA Redlining Analysis
            </h3>
            <p className="text-xs text-gray-400 mt-0.5">
              Application and origination disparities in majority-minority census tracts
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex gap-1">
            {R1_YEARS.map(y => (
              <button
                key={y}
                onClick={() => toggleYear(y)}
                className={clsx(
                  'rounded px-2 py-1 text-xs font-medium transition-colors',
                  selectedYears.includes(y)
                    ? 'bg-indigo-100 text-indigo-700'
                    : 'bg-gray-100 text-gray-500 hover:bg-gray-200',
                )}
              >
                {y}
              </button>
            ))}
          </div>
          <button
            onClick={() => ingestMutation.mutate()}
            disabled={ingestMutation.isPending}
            className="inline-flex items-center gap-1.5 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-1.5 text-xs font-medium text-indigo-700 hover:bg-indigo-100 disabled:opacity-50"
          >
            <ArrowPathIcon className={clsx('h-3.5 w-3.5', ingestMutation.isPending && 'animate-spin')} />
            {ingestMutation.isPending ? 'Ingesting...' : 'Ingest VBAA Data'}
          </button>
        </div>
      </div>

      {r1Loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-indigo-600" />
        </div>
      ) : !r1?.rows?.length ? (
        <div className="px-4 py-8 text-center">
          <p className="text-sm text-gray-500">
            No R1 data available. Click &quot;Ingest VBAA Data&quot; to download CFPB aggregate data and compute the VBAA peer comparison.
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          {/* Applications Table */}
          <R1Table
            title="HMDA Applications"
            subtitle="Action Taken = 1, 2, 3, 4, 5, 7, 8 (excludes purchased loans)"
            grouped={grouped}
            years={sortedYears}
            mode="app"
            subjectLabel={subjectLabel}
            subjectName={subjectName}
          />

          {/* Originations Table */}
          <R1Table
            title="HMDA Originations"
            subtitle="Action Taken = 1"
            grouped={grouped}
            years={sortedYears}
            mode="orig"
            subjectLabel={subjectLabel}
            subjectName={subjectName}
          />

          {/* Data Source Note */}
          {r1.rows.some((r: any) => r.dataSource === 'PRELIMINARY') && (
            <div className="border-t bg-amber-50 px-4 py-2">
              <p className="text-xs text-amber-700">
                Some years use preliminary HMDA aggregate data. Results will automatically update when CFPB releases final data.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function R1Table({
  title,
  subtitle,
  grouped,
  years,
  mode,
  subjectLabel,
  subjectName,
}: {
  title: string;
  subtitle: string;
  grouped: Map<string, any[]>;
  years: number[];
  mode: 'app' | 'orig';
  subjectLabel: string;
  subjectName: string;
}) {
  const bankTotalKey = mode === 'app' ? 'bankAppTotal' : 'bankOrigTotal';
  const bankMmctKey = mode === 'app' ? 'bankAppMmct' : 'bankOrigMmct';
  const bankRateKey = mode === 'app' ? 'bankAppMmctRate' : 'bankOrigMmctRate';
  const vbaaTotalKey = mode === 'app' ? 'vbaaAppTotal' : 'vbaaOrigTotal';
  const vbaaMmctKey = mode === 'app' ? 'vbaaAppMmct' : 'vbaaOrigMmct';
  const vbaaRateKey = mode === 'app' ? 'vbaaAppMmctRate' : 'vbaaOrigMmctRate';
  const dispKey = mode === 'app' ? 'appDisparity' : 'origDisparity';
  const sigKey = mode === 'app' ? 'appSignificance' : 'origSignificance';

  return (
    <div className="border-t">
      <div className="bg-gray-50 px-4 py-2 border-b">
        <p className="text-xs font-semibold text-gray-700">{title}</p>
        <p className="text-[10px] text-gray-400">{subtitle}</p>
      </div>
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b bg-gray-50">
            <th className="px-3 py-2 text-left font-semibold text-gray-600 w-40">Assessment Area</th>
            <th className="px-3 py-2 text-left font-semibold text-gray-600 w-24">Minority Concentration</th>
            {years.map(y => (
              <th key={y} colSpan={6} className="px-1 py-2 text-center font-semibold text-gray-600 border-l">
                {y} HMDA {mode === 'app' ? 'Apps' : 'Orig'}
              </th>
            ))}
          </tr>
          <tr className="border-b bg-gray-50">
            <th className="px-3 py-1" />
            <th className="px-3 py-1" />
            {years.map(y => (
              <th key={y} className="contents">
                <th className="px-2 py-1 text-right font-medium text-gray-500 border-l" title={subjectName}>{subjectLabel}</th>
                <th className="px-2 py-1 text-right font-medium text-gray-500">%</th>
                <th className="px-2 py-1 text-right font-medium text-gray-500">VBAA</th>
                <th className="px-2 py-1 text-right font-medium text-gray-500">%</th>
                <th className="px-2 py-1 text-right font-medium text-gray-500">Diff.</th>
                <th className="px-2 py-1 text-right font-medium text-gray-500">Sig.</th>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from(grouped.entries()).map(([aaName, rows]) => (
            <>
              {/* NMCT row (0-50%) */}
              <tr key={`${aaName}-nmct`} className="border-b hover:bg-gray-50">
                <td className="px-3 py-2 font-medium text-gray-900" rowSpan={2}>{aaName}</td>
                <td className="px-3 py-2 text-gray-600">0-50.00%</td>
                {years.map(y => {
                  const row = rows.find((r: any) => r.dataYear === y);
                  if (!row) return <td key={y} colSpan={6} className="px-2 py-2 text-center text-gray-300 border-l">—</td>;
                  const bankNmct = row[bankTotalKey] - row[bankMmctKey];
                  const bankNmctRate = row[bankTotalKey] > 0 ? bankNmct / row[bankTotalKey] : 0;
                  const vbaaNmct = row[vbaaTotalKey] - row[vbaaMmctKey];
                  const vbaaNmctRate = row[vbaaTotalKey] > 0 ? vbaaNmct / row[vbaaTotalKey] : 0;
                  return (
                    <td key={y} className="contents">
                      <td className="px-2 py-2 text-right tabular-nums border-l">{bankNmct}</td>
                      <td className="px-2 py-2 text-right tabular-nums">{pct(bankNmctRate)}</td>
                      <td className="px-2 py-2 text-right tabular-nums">{vbaaNmct}</td>
                      <td className="px-2 py-2 text-right tabular-nums">{pct(vbaaNmctRate)}</td>
                      <td className="px-2 py-2 text-right text-green-600">-</td>
                      <td className="px-2 py-2 text-right text-gray-400">-</td>
                    </td>
                  );
                })}
              </tr>
              {/* MMCT row (50.01-100%) */}
              <tr key={`${aaName}-mmct`} className="border-b hover:bg-gray-50">
                <td className="px-3 py-2 text-gray-600">50.01-100%</td>
                {years.map(y => {
                  const row = rows.find((r: any) => r.dataYear === y);
                  if (!row) return <td key={y} colSpan={6} className="px-2 py-2 text-center text-gray-300 border-l">—</td>;
                  const disp = row[dispKey];
                  const sig = row[sigKey];
                  // Backend stores disparity as a non-negative magnitude; sig === 'NA' means
                  // the bank lends MORE to MMCTs than peers (favorable). Restore Wallace's
                  // signed convention exactly like the FLD R1 screen
                  // (components/fld/R1Distribution.tsx) so both screens agree: favorable shows
                  // a leading "-" in green; an actual shortfall shows positive in red.
                  const displayDiff = disp == null ? null : (sig === 'NA' ? -disp : disp);
                  const favorable = displayDiff != null && displayDiff <= 0;
                  return (
                    <td key={y} className="contents">
                      <td className="px-2 py-2 text-right tabular-nums border-l">{row[bankMmctKey]}</td>
                      <td className="px-2 py-2 text-right tabular-nums">{pct(row[bankRateKey])}</td>
                      <td className="px-2 py-2 text-right tabular-nums">{row[vbaaMmctKey]}</td>
                      <td className="px-2 py-2 text-right tabular-nums">{pct(row[vbaaRateKey])}</td>
                      <td className={clsx(
                        'px-2 py-2 text-right tabular-nums font-medium',
                        displayDiff == null ? 'text-gray-400' : favorable ? 'text-green-600' : 'text-red-600',
                      )}>
                        {displayDiff == null ? '-' : `${displayDiff < 0 ? '-' : ''}${Math.abs(displayDiff).toFixed(2)}`}
                      </td>
                      <td className={clsx(
                        'px-2 py-2 text-right font-medium',
                        sig === '<1%' ? 'text-red-600' : sig === '<5%' ? 'text-amber-600' : 'text-gray-400',
                      )}>
                        {sig ?? '-'}
                      </td>
                    </td>
                  );
                })}
              </tr>
            </>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function pct(value: number): string {
  // Two decimals to match the FLD / Fair Lending approach (Wallace 2026-06-11).
  return `${(value * 100).toFixed(2)}%`;
}
